import React from 'react';
import { Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Bar, ComposedChart, CartesianGrid } from 'recharts';
import { Loader2, TrendingUp, TrendingDown, Minus, RefreshCcw, Layers } from 'lucide-react';
import BiasBadge from './BiasBadge';
import { RecommendationBadge } from './RecommendationBadge';
import { VerdictBanner } from './Verdict';
import DataFreshness from './DataFreshness';
import LivePrice from './LivePrice';
import { useCoinData } from '../../lib/useCoinData';

const MtfLine = ({ data }) => {
    const st = data.multi_tf_status;
    const mt = data.multi_tf;
    const other = data.multi_tf_other_action || 'BEKLE';
    const own = data.recommendation?.action || 'WAIT';
    let cls = 'text-slate-300 border-slate-600 bg-slate-700/40';
    let text = '4 saatlik analiz verisi bekleniyor';
    if (st === 'aligned' && mt) {
        cls = mt.tier === 'STRONG' ? 'text-rose-100 border-rose-400/50 bg-rose-500/10' : 'text-amber-100 border-amber-400/40 bg-amber-500/10';
        text = `Çoklu zaman: ${mt.tier === 'STRONG' ? 'GÜÇLÜ' : 'ORTA'} ${mt.action} — 15 dk %${mt.short_confidence} + 4 saat %${mt.medium_confidence} aynı yönde`;
    } else if (st === 'conflict') {
        cls = 'text-amber-100 border-amber-500/50 bg-amber-500/15';
        text = `⚠ Çoklu zaman ÇELİŞKİLİ: 15 dk ${own} diyor, 4 saat ${other} diyor. İşlem açma, netleşmesini bekle.`;
    } else if (st === 'not_aligned' && own === other && own !== 'WAIT') {
        text = `Aynı yön (${own}) ama güven alarm eşiğinin altında: 15 dk %${data.recommendation?.confidence ?? 0}, 4 saat %${data.multi_tf_other_confidence ?? 0}. Alarm için ikisi de en az %55 (GÜÇLÜ için %70) olmalı.`;
    } else if (st === 'not_aligned') {
        text = `Çoklu zaman uyumu yok: 15 dk ${own === 'WAIT' ? 'BEKLE' : own}, 4 saat ${other === 'WAIT' ? 'BEKLE' : other}`;
    }
    return (
        <div data-testid="coin-detail-mtf" className={`mt-3 text-[11px] border rounded-sm px-2 py-1.5 flex items-start gap-1.5 ${cls}`}>
            <Layers className="h-3.5 w-3.5 shrink-0 mt-px" /> <span>{text}</span>
        </div>
    );
};

// Stable chart style constants (defined outside component to avoid re-creation per render)
const AXIS_TICK = { fill: '#52525b', fontSize: 10, fontFamily: 'JetBrains Mono' };
const TOOLTIP_CONTENT_STYLE = { background: '#000', border: '1px solid #27272a', borderRadius: '4px', fontFamily: 'JetBrains Mono', fontSize: 11 };
const TOOLTIP_LABEL_STYLE = { color: '#a1a1aa' };
const TOOLTIP_ITEM_STYLE = { color: '#fafafa' };
const Y_DOMAIN_AUTO = ['auto', 'auto'];

const fmt = (v, decimals = 2) => {
    if (v === null || v === undefined || Number.isNaN(v)) return '—';
    return Number(v).toLocaleString('en-US', { maximumFractionDigits: decimals });
};
const fmtPrice = (v) => {
    if (!v && v !== 0) return '—';
    if (v >= 1000) return fmt(v, 2);
    if (v >= 1) return fmt(v, 4);
    return fmt(v, 8);
};

const Metric = ({ label, value, valueClass = 'text-slate-100', testid }) => (
    <div className="flex flex-col gap-1" data-testid={testid}>
        <span className="text-[10px] uppercase tracking-[0.1em] text-slate-400">{label}</span>
        <span className={`text-sm font-mono tabular-nums ${valueClass}`}>{value}</span>
    </div>
);

const TrendIcon = ({ dir }) => {
    if (dir === 'bullish') return <TrendingUp className="h-3.5 w-3.5 text-emerald-400" />;
    if (dir === 'bearish') return <TrendingDown className="h-3.5 w-3.5 text-rose-500" />;
    return <Minus className="h-3.5 w-3.5 text-slate-400" />;
};

export default function CoinDetail({ symbol, timeframe, tick, listVerdict }) {
    const { data, loading, error, reload } = useCoinData(symbol, timeframe, 15000);

    if (!symbol) {
        return (
            <div className="border border-slate-700 bg-slate-800 rounded-md p-8 h-full flex items-center justify-center text-sm text-slate-400" data-testid="coin-detail-empty">
                Bir coin seçerek detay görüntüle
            </div>
        );
    }

    const up = (data?.change_pct_24h ?? 0) >= 0;
    const candles = (data?.candles || []).map((c) => ({
        time: new Date(c.time).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
        close: c.close,
        volume: c.volume,
    }));

    const dir = data?.trend?.direction;

    return (
        <div className="border border-slate-700 bg-slate-800 rounded-md overflow-hidden h-full" data-testid="coin-detail-card">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700">
                <div className="flex items-center gap-3">
                    <h3 className="text-base font-semibold tracking-tight text-slate-50">{symbol.replace('USDT', '')}<span className="text-slate-400 text-xs ml-1">/USDT</span></h3>
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 border-l border-slate-700 pl-3">
                        {timeframe === 'short' ? '15m' : '4h'} · Detay
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    {data && <DataFreshness at={data.analyzed_at} quality={data.data_quality} testid="coin-detail-freshness" />}
                    {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
                </div>
            </div>

            {!data && listVerdict && (
                <div className="px-4 sm:px-5 pt-4">
                    <VerdictBanner v={listVerdict} symbol={symbol} testId="coin-verdict-banner" />
                </div>
            )}
            {!data && loading && (
                <div data-testid="coin-detail-loading" className="px-5 py-10 flex items-center gap-2 text-xs text-slate-400">
                    <Loader2 className="h-4 w-4 animate-spin" /> {symbol.replace('USDT', '')} verisi alınıyor…
                </div>
            )}
            {!data && !loading && error && (
                <div data-testid="coin-detail-error" className="px-5 py-8 text-xs text-rose-300 flex items-center gap-3">
                    {symbol.replace('USDT', '')} verisi alınamadı: {error}
                    <button onClick={reload} data-testid="coin-detail-retry" className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-slate-600 text-slate-200 hover:bg-slate-700">
                        <RefreshCcw className="h-3 w-3" /> Tekrar dene
                    </button>
                </div>
            )}

            {data && (
                <>
                    <div className="px-5 py-4 border-b border-slate-700 flex items-end gap-4">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.1em] text-slate-400 mb-1">Son Fiyat</div>
                            <div data-testid="coin-detail-price" className={`text-3xl font-mono font-medium leading-none tracking-tight ${up ? 'text-emerald-400 ticker-glow-green' : 'text-rose-500 ticker-glow-red'}`}>
                                <LivePrice symbol={symbol} tick={tick} fallbackPrice={data.price} />
                            </div>
                        </div>
                        <div className={`pb-1 text-xs font-mono ${up ? 'text-emerald-400' : 'text-rose-500'}`}>
                            {up ? '+' : ''}{fmt(data.change_pct_24h, 2)}% (24s)
                        </div>
                    </div>

                    <div className="px-4 sm:px-5 py-4 border-b border-slate-700" data-testid="recommendation-section">
                        <VerdictBanner v={listVerdict || data.verdict} symbol={symbol} testId="coin-verdict-banner" />
                        <div className="text-[10px] uppercase tracking-[0.1em] text-slate-400 mt-3 mb-2">Göstergelerin eğilimi <span className="normal-case tracking-normal text-slate-500">— ikincil bilgi; işlem planı için yukarıdaki KARAR geçerlidir</span></div>
                        <RecommendationBadge rec={data.recommendation} compact={false} secondary />
                        <MtfLine data={data} />
                    </div>

                    <div className="px-4 sm:px-5 py-4 border-b border-slate-700 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 items-stretch" data-testid="bias-section">
                        <div className="md:col-span-1">
                            <div className="text-[10px] uppercase tracking-[0.1em] text-slate-400 mb-2">Yön Eğilimi</div>
                            <BiasBadge bias={data.bias} />
                            <p className="text-[10px] text-slate-400 mt-2 leading-relaxed">
                                Bu, "long/short gir" sinyali değildir. Mevcut piyasa koşullarının hangi yöne eğilimli
                                olduğunu, kombine sinyallere göre özetler. Geçersizlik için aşağıdaki destek/direnç
                                kapanışlarını izle.
                            </p>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.1em] text-emerald-400/80 mb-2">
                                ▲ Yukarı Sinyaller ({data.bias?.long_signals?.length || 0})
                            </div>
                            <ul className="space-y-1 text-[11px] text-slate-200">
                                {(data.bias?.long_signals || []).map((s) => (
                                    <li key={`ls-${s}`} className="leading-snug before:content-['+'] before:text-emerald-400 before:mr-1.5">{s}</li>
                                ))}
                                {(!data.bias?.long_signals || data.bias.long_signals.length === 0) && (
                                    <li className="text-slate-400">Aktif yukarı yönlü sinyal yok.</li>
                                )}
                            </ul>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.1em] text-rose-400/80 mb-2">
                                ▼ Aşağı Sinyaller ({data.bias?.short_signals?.length || 0})
                            </div>
                            <ul className="space-y-1 text-[11px] text-slate-200">
                                {(data.bias?.short_signals || []).map((s) => (
                                    <li key={`ss-${s}`} className="leading-snug before:content-['−'] before:text-rose-400 before:mr-1.5">{s}</li>
                                ))}
                                {(!data.bias?.short_signals || data.bias.short_signals.length === 0) && (
                                    <li className="text-slate-400">Aktif aşağı yönlü sinyal yok.</li>
                                )}
                            </ul>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 px-5 py-4 border-b border-slate-700">
                        <Metric label="Hacim 24s" value={`$${fmt((data.volume_24h || 0) / 1e6, 2)}M`} testid="metric-volume" />
                        <Metric
                            label="Fonlama %"
                            value={fmt(data.derivatives?.funding_rate_pct, 4)}
                            valueClass={(data.derivatives?.funding_rate_pct ?? 0) > 0 ? 'text-rose-400' : 'text-emerald-400'}
                            testid="metric-funding"
                        />
                        <Metric label="L/S Oranı" value={data.derivatives?.long_short_ratio ? fmt(data.derivatives.long_short_ratio, 2) : 'veri yok'} testid="metric-ls" />
                        <Metric
                            label="OI Δ %"
                            value={fmt(data.derivatives?.oi_change_pct, 2)}
                            valueClass={(data.derivatives?.oi_change_pct ?? 0) >= 0 ? 'text-emerald-400' : 'text-rose-500'}
                            testid="metric-oi"
                        />
                        <Metric label="RSI" value={fmt(data.indicators?.rsi, 1)} testid="metric-rsi" />
                        <Metric label="Volatilite %" value={fmt(data.indicators?.volatility_pct, 2)} testid="metric-vol" />
                        <Metric label="Destek" value={`$${fmtPrice(data.indicators?.support)}`} valueClass="text-emerald-400" testid="metric-support" />
                        <Metric label="Direnç" value={`$${fmtPrice(data.indicators?.resistance)}`} valueClass="text-rose-400" testid="metric-resistance" />
                    </div>

                    <div className="px-5 py-4 border-b border-slate-700 flex items-center gap-6 text-xs">
                        <div className="flex items-center gap-2">
                            <TrendIcon dir={dir} />
                            <span className="text-slate-300 uppercase tracking-wider text-[10px]">Trend</span>
                            <span className="text-slate-100 font-medium">
                                {dir === 'bullish' ? 'Yukarı' : dir === 'bearish' ? 'Aşağı' : 'Nötr'} · {fmt(data.trend?.strength, 1)}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-slate-400 text-[10px] uppercase tracking-wider">BB Sıkışması</span>
                            <span className={`text-xs ${data.indicators?.bb_squeeze ? 'text-amber-400' : 'text-slate-400'}`}>
                                {data.indicators?.bb_squeeze ? 'EVET' : 'HAYIR'}
                            </span>
                        </div>
                    </div>

                    <div className="h-56 px-2 py-3">
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={candles}>
                                <defs>
                                    <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={up ? '#34d399' : '#f43f5e'} stopOpacity={0.35} />
                                        <stop offset="100%" stopColor={up ? '#34d399' : '#f43f5e'} stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="2 4" stroke="#27272a" vertical={false} />
                                <XAxis dataKey="time" tick={AXIS_TICK} axisLine={false} tickLine={false} minTickGap={40} />
                                <YAxis yAxisId="price" orientation="right" tick={AXIS_TICK} axisLine={false} tickLine={false} domain={Y_DOMAIN_AUTO} />
                                <YAxis yAxisId="vol" orientation="left" hide domain={Y_DOMAIN_AUTO} />
                                <Tooltip
                                    contentStyle={TOOLTIP_CONTENT_STYLE}
                                    labelStyle={TOOLTIP_LABEL_STYLE}
                                    itemStyle={TOOLTIP_ITEM_STYLE}
                                />
                                <Bar yAxisId="vol" dataKey="volume" fill="#3f3f46" opacity={0.6} />
                                <Area yAxisId="price" type="monotone" dataKey="close" stroke={up ? '#34d399' : '#f43f5e'} strokeWidth={1.5} fill="url(#priceGrad)" />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </>
            )}
        </div>
    );
}
